import { AxiosInstance } from "axios";
import { BaseClient } from "../base";
import { ClientConfig } from "../../types";

import {
  AuthorizationRequest,
  AuthorizationResponse,
  AuthorizationRequestFields,
  GoogleAuthorizationRequest,
  GoogleAuthFields,
} from ".";

import {
  ConfigurationEconde,
  configurationEncoder,
} from "./configurationEncode";
import { CryptoJSHelper } from "./cryptoJSHelper";

export interface IAuthClient<
  TAuthResponse extends AuthorizationResponse = AuthorizationResponse
> {
  /**
   * Normal authentication with form values.
   * @endpoint `/auth`
   * @param requestParams Authentication credentials.
   */
  auth: <TAuthRequest extends AuthorizationRequest = AuthorizationRequest>(
    requestParams: TAuthRequest
  ) => Promise<TAuthResponse>;

  /**
   * Authentication using the Google OAuth API.
   * @endpoint `/auth/google`
   * @param requestParams Google authentication credentials.
   */
  authGoogle: (
    requestParams: GoogleAuthorizationRequest
  ) => Promise<TAuthResponse>;
}

export class AuthClient<
  TAuthResponse extends AuthorizationResponse = AuthorizationResponse
>
  extends BaseClient
  implements IAuthClient<TAuthResponse>
{
  private endpoint = "/auth";

  constructor(axiosInstance: AxiosInstance, clientConfig?: ClientConfig) {
    super(axiosInstance, clientConfig);
  }

  private getEndpoint(url: string) {
    return `${this.endpoint}${url}`;
  }

  public async auth<
    TAuthRequest extends AuthorizationRequest = AuthorizationRequest
  >(requestParams: TAuthRequest): Promise<TAuthResponse> {
    const encoder: ConfigurationEconde =
      await configurationEncoder.getConfiguration();
    const encryptPassword = CryptoJSHelper.encrypt(
      encoder,
      requestParams[AuthorizationRequestFields.Password]
    );

    return this.post<TAuthRequest, never, TAuthResponse>(
      this.getEndpoint(""),
      {
        ...requestParams,
        [AuthorizationRequestFields.Password]: encryptPassword,
      },
      {}
    );
  }

  public async authGoogle(
    requestParams: GoogleAuthorizationRequest
  ): Promise<TAuthResponse> {
    return this.post(
      this.getEndpoint("/google"),
      {
        [GoogleAuthFields.Token]: requestParams.token,
      },
      {}
    );
  }
}
